// src/components/Home.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe,
  Users,
  Zap,
  MessageCircle,
  ArrowRight,
  Wallet,
  Cpu,
  Coffee,
  RefreshCw,
  Wrench,
  ShieldCheck
} from 'lucide-react';
import { signInWithPopup } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase';
import VisitStats from './VisitStats';
import Chatboard from './Chatboard/Chatboard';
import Newsboard from './Newsboard/Newsboard';
import WelcomeIntro from './WelcomeIntro/WelcomeIntro';
import Dapp from './Dapp';
import MyAI from './MyAI/MyAI';
import WorldlandBackground from './WorldlandBackground/WorldlandBackground';
import './Home.css';

const Home = ({
  user,
  userProfile,
  setIsNicknameSetupOpen,
  openChatWindow
}) => {
  const [authLoading, setAuthLoading] = useState(false);
  const [cafePoints, setCafePoints] = useState(0);
  const [isLoadingPoints, setIsLoadingPoints] = useState(false);
  const [pointsLastUpdated, setPointsLastUpdated] = useState(null);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('welcome');

  const isINSSA = user?.level >= 4;

  // 관리자 권한 확인
  const isAdmin = user?.email === 'gategil1149@gmail.com' || user?.email === 'worldlandcafe@gmail.com';

  const features = [
    {
      icon: Users,
      title: '활발한 커뮤니티',
      description: '다양한 배경을 가진 개발자, 투자자, 연구자들이 모여 지식을 나누고 성장합니다.',
      onClick: () => navigate('/community')
    },
    {
      icon: Zap,
      title: '최신 기술',
      description: '블록체인의 최신 트렌드와 기술 발전을 실시간으로 경험하고 학습할 수 있습니다.',
      onClick: () => navigate('/technology')
    },
    {
      icon: MessageCircle,
      title: '실시간 채팅',
      description: '커뮤니티 멤버들과 실시간으로 소통하고 정보를 교환할 수 있습니다.',
      onClick: handleChatClick
    }
  ];

  const newsItems = [
    {
      title: 'CAFE Points 관리 시스템 추가!',
      date: '2025.07.15',
      content: '관리자가 사용자의 CAFE Points를 효율적으로 관리할 수 있는 시스템을 추가했습니다.'
    },
    {
      title: 'CAFE Points 시스템 도입!',
      date: '2025.07.15',
      content: '에어드랍 권한을 CAFE Points로 관리하는 새로운 시스템을 도입했습니다.'
    },
    {
      title: 'WorldLand 구글 로그인 지원!',
      date: '2025.07.05',
      content: '이제 구글 계정으로 간편하게 로그인할 수 있습니다.'
    },
    {
      title: 'WorldLand 채팅 서비스 출시!',
      date: '2025.07.05',
      content: '실시간 커뮤니티 채팅 기능이 새롭게 추가되었습니다.'
    }
  ];

  // CAFE Points 조회 함수
  const fetchCafePoints = async () => {
    if (!user?.email) {
      setCafePoints(0);
      return;
    }

    setIsLoadingPoints(true);
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
      const response = await fetch(
        `${serverUrl}/api/cafe/points/balance?email=${encodeURIComponent(user.email)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setCafePoints(data.cafePoints || 0);
      setPointsLastUpdated(data.lastUpdated);

      console.log('✅ CAFE Points 조회 성공:', data);

    } catch (error) {
      console.error('❌ CAFE Points 조회 실패:', error);
      setCafePoints(0);
    } finally {
      setIsLoadingPoints(false);
    }
  };

  // 사용자 로그인 시 CAFE Points 조회
  useEffect(() => {
    if (user?.email) {
      fetchCafePoints();
    } else {
      setCafePoints(0);
      setPointsLastUpdated(null);
    }
  }, [user?.email]);

  // 구글 로그인
  const handleGoogleLogin = async () => {
    setAuthLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      console.log('로그인된 사용자:', user);

      // 기존 사용자 프로필 확인
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (!userDoc.exists()) {
        // 새 사용자인 경우 닉네임 설정 팝업 열기
        setIsNicknameSetupOpen(true);
      }
    } catch (error) {
      console.error('로그인 오류:', error);
      alert('로그인 중 오류가 발생했습니다.');
    } finally {
      setAuthLoading(false);
    }
  };

  // 채팅 열기 (로그인 및 닉네임 확인)
  function handleChatClick() {
    if (!user) {
      handleGoogleLogin();
      return;
    }

    if (!userProfile?.nickname) {
      setIsNicknameSetupOpen(true);
      return;
    }

    // 새 창에서 채팅 열기
    openChatWindow();
  }

  // Dapp 페이지로 이동
  const handleDappClick = () => {
    navigate('/dapp');
  };

  // 피처 카드 클릭 핸들러
  const handleFeatureClick = (feature) => {
    if (feature.onClick) {
      feature.onClick();
    }
  };

  const handleUtilsClick = () => {
    navigate('/utils');
  };

  return (
    <div className="home">
      <WorldlandBackground />
      {/* Hero Section */}
      <div className="hero-section">
        {/* <div className="hero-badge">
          <div className="badge-dot"></div>
          <span className="badge-text">WorldLand 커뮤니티에 오신 것을 환영합니다</span>
        </div> */}

        <h1 className="hero-title">
          <span className="hero-title-gradient">
            월드랜드의 미래를 함께 만들어가요
          </span>
        </h1>

        <div className="hero-actions">
          <button
            onClick={() => setActiveTab('welcome')}
            className="hero-btn"
          >
            <Wallet className="hero-btn-icon" />
            <span>Welcome</span>
          </button>
          <button
            onClick={() => setActiveTab('board')}
            className="hero-btn"
          >
            <Wallet className="hero-btn-icon" />
            <span>게시판</span>
          </button>
          <button
            onClick={() => navigate('/Dapp')}
            // onClick={() => setActiveTab('dapp')}
            className="hero-btn"
          >
            <Cpu className="hero-btn-icon" />
            <span>Dapp</span>
          </button>
          <button
            onClick={() => setActiveTab('myai')}
            className="hero-btn"
          >
            <Cpu className="hero-btn-icon" />
            <span>MyAI</span>
          </button>
          {/* {isAdmin && (
            <button 
              onClick={handleUtilsClick}
              className="hero-btn"
            >
              <Wrench className="hero-btn-icon" />
              <span>Util</span>
            </button>
          )} */}
          {/* {isAdmin && (
            <button 
              onClick={() => navigate('/admin-menu')}
              className="hero-btn admin-highlight"
            >
              <ShieldCheck className="hero-btn-icon" />
              <span>관리</span>
            </button>
          )} */}
        </div>
      </div>

      {activeTab === 'board' && (
        <div className="chatboard-section">
          <Chatboard
            user={user}
            userProfile={userProfile}
          />
        </div>
      )}

      {/* News Section */}
      {activeTab === 'welcome' && (
        <div className="welcome-section">
          <WelcomeIntro
            user={user}
            userProfile={userProfile}
            setActiveTab={setActiveTab}
          />
        </div>
      )}

      {activeTab === 'dapp' && (
        <div className="dapp-intro-section">
          <Dapp
            user={user}
            userProfile={userProfile}
          />
        </div>
      )}

      {activeTab === 'myai' && user && isINSSA && (
        <div className="myai-section">
          <MyAI
            user={user}
            userProfile={userProfile}
          />
        </div>
      )}

      {/* CAFE Points 표시 섹션 */}
      {activeTab === 'welcome' && user && (
        <div className="cafe-points-section">
          <div className="cafe-points-card">
            <div className="cafe-points-header">
              <div className="cafe-points-icon">
                <Coffee className="icon" />
              </div>
              <div className="cafe-points-info">
                <span className="cafe-points-label">보유 CAFE Points</span>
                <div className="cafe-points-value">
                  {isLoadingPoints ? (
                    <div className="cafe-points-loading">
                      <div className="spinner-small"></div>
                      <span>조회 중...</span>
                    </div>
                  ) : (
                    <>
                      <span className="points-number">{cafePoints.toLocaleString()}</span>
                      <span className="points-unit">Points</span>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={fetchCafePoints}
                className="refresh-points-btn"
                disabled={isLoadingPoints}
                title="포인트 새로고침"
              >
                <RefreshCw className={`icon ${isLoadingPoints ? 'spinning' : ''}`} />
              </button>
            </div>

            <div className="cafe-points-description">
              <p className="no-points-msg">
                CAFE Token을 CAFE Points로 1:1 교환 가능합니다.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 방문자 통계 섹션 추가 */}
      <div className="visit-stats-section">
        <VisitStats
          pageName="home"
          user={user}
          className="compact"
        />
      </div>

      {/* Features Section */}
      {/* {activeTab === 'home' && (
        <div className="features-section">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className={`feature-card ${feature.onClick ? 'clickable' : ''}`}
              onClick={() => handleFeatureClick(feature)}
            >
              <div className="feature-icon">
                <feature.icon className="icon" />
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      )} */}


    </div>
  );
};

export default Home;